import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import {
  SimpleChange,
  EventEmitter,
  Component,
  OnChanges,
  OnDestroy,
  Output,
  Input,
} from "@angular/core";

// Environments
import { environment } from "src/environments/environment";

// Libraries
import * as _ from "underscore";
import * as $ from "jquery";

// Models
import { TriggerLoginGameDetails } from "src/app/modules/shared/models/trigger-login/trigger-login-game-details.model";
import { GamePregmatic } from "src/app/modules/game-groups/models/game.model";

// Pipes
import { CurrencyFormatPipe } from "src/app/modules/shared/pipes/currency-format.pipe";

// Services
import { GamePlayService } from "src/app/modules/game-groups/services/game-play.service";
import { TranslationService } from "src/app/modules/shared/services/translation.service";
import { UserDetailsService } from "src/app/modules/user/services/user-details.service";
import { JackpotService } from "src/app/modules/game-groups/services/jackpot.service";
import { MixPanelService } from "src/app/modules/shared/services/mix-panel.service";
import { CommonService } from "src/app/modules/shared/services/common.service";
import { UtilityService } from "src/app/utility/utility.service";

@Component({
  selector: "app-game-card",
  templateUrl: "./game-card.component.html",
  styleUrls: ["./game-card.component.scss"],
})
export class GameCardComponent implements OnChanges, OnDestroy {
  // Inputs
  @Input() game_asset_style: string = "";
  @Input() isJackpot: boolean = false;
  @Input() gameGroupName: string = "";
  @Input() activeTagName: string = "";
  @Input() callingFrom: string = "";
  @Input() lobbyName: string = "";

  @Input() gameData: GamePregmatic;

  // Outputs
  @Output() callBackAction: EventEmitter<void> = new EventEmitter<void>();

  // Numbers
  gameLauncherTimeout: number = 0;
  timeout: number = 0;

  // Strings
  gamesUrlPath: string = environment.gamesUrlPath;
  currencySymbol: string = "";
  imageStypeTag: string = "";
  languageCode: string = "";
  imgixParams: string = "";

  // Booleans
  isLoggedIn: boolean = false;
  canLoggedUserPlayForFun = environment.features.game.loggedPlayForFun;

  // Others
  /*
   Below all are the basic assest type's which decide How the game card
   should be displayed (mostly in terms of size) & image size both
  
   Basically use this constant values to attach at end of the game image url
   to render proper image from CMS
  */
  imageEndTagKeyValue: {
    [key: string]: string;
  } = {
    poster: "_poster",
    "default-casino": "_casino",
    "default-live-casino": "_default_live_casino",
    "poster-jackpot": "_poster",
    "poster-live-casino": "_poster",
  };
  jackpotKeyValue: {
    [key: string]: number;
  } = {};

  // Subscriptions
  mircoGamingJackpotDataSubscription: Subscription;

  subscriptions: Subscription[] = [];

  constructor(
    private translationService: TranslationService,
    private userDetailsService: UserDetailsService,
    private gamePlayService: GamePlayService,
    private currencyPipe: CurrencyFormatPipe,
    private mixpanelService: MixPanelService,
    private jackpotService: JackpotService,
    private commonService: CommonService,
    private utility: UtilityService,
    private router: Router
  ) {}

  // -----------------------------------------------------------------
  // Lifecycle Hooks
  ngOnInit(): void {
    this.isLoggedIn = this.utility.isUserLoggedIn();

    this.imgixParams = this.utility.getImgixParams();

    this.subscriptions = [
      this.commonService.loginComplete$.subscribe((isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;
      }),
      this.commonService.logOutComplete$.subscribe(() => {
        this.isLoggedIn = false;
      }),
      this.translationService.langCodeSb$.subscribe((languageCode: string) => {
        this.languageCode = languageCode;
      }),
      this.userDetailsService.currencySymbolSb$.subscribe(
        (currencySymbol: string) => {
          this.currencySymbol = currencySymbol;
        }
      ),
    ];

    if (this.isJackpot) {
      this.mircoGamingJackpotDataSubscription = this.jackpotService.mircoGamingJackpotData$.subscribe(
        (jackpotKeyValue: { [key: string]: number }) => {
          this.jackpotKeyValue = jackpotKeyValue;

          this.timeout = setTimeout(() => this.onJackpotNumberCount());
        }
      );
    }
  }

  ngOnChanges(changes: { [propName: string]: SimpleChange }): void {
    if (
      changes["gameData"] &&
      changes["gameData"].previousValue !== changes["gameData"].currentValue
    ) {
      this.gameData = changes["gameData"].currentValue;
    }

    if (
      changes["isJackpot"] &&
      changes["isJackpot"].previousValue !== changes["isJackpot"].currentValue
    ) {
      this.isJackpot = changes["isJackpot"].currentValue;
    }

    if (
      changes["game_asset_style"] &&
      changes["game_asset_style"].previousValue !==
        changes["game_asset_style"].currentValue
    ) {
      let styleTag: string = changes["game_asset_style"].currentValue;

      this.imageStypeTag = this.imageEndTagKeyValue[styleTag];
    }

    if (
      changes["callingFrom"] &&
      changes["callingFrom"].previousValue !==
        changes["callingFrom"].currentValue
    ) {
      this.callingFrom = changes["callingFrom"].currentValue;
    }

    if (
      changes["gameGroupName"] &&
      changes["gameGroupName"].previousValue !==
        changes["gameGroupName"].currentValue
    ) {
      this.gameGroupName = changes["gameGroupName"].currentValue;
    }

    if (
      changes["lobbyName"] &&
      changes["lobbyName"].previousValue !== changes["lobbyName"].currentValue
    ) {
      this.lobbyName = changes["lobbyName"].currentValue;
    }

    if (
      changes["activeTagName"] &&
      changes["activeTagName"].previousValue !==
        changes["activeTagName"].currentValue
    ) {
      this.activeTagName = changes["activeTagName"].currentValue;
    }
  }

  ngAfterViewInit(): void {
    window["prerenderReady"] = true;
  }

  // -----------------------------------------------------------------
  // Get Methods
  getIsRealGame(gametype: string): boolean {
    return gametype === "realgame" && this.isLoggedIn;
  }

  getIsDemoGame(gametype: string, hasDemo: boolean): boolean {
    return gametype === "freegame" && hasDemo;
  }

  // -----------------------------------------------------------------
  // Set Methods
  onGameLauncher(
    event: Event,
    gameName: string,
    hasDemo: boolean,
    gametype: string
  ): void {
    if (event) {
      event.stopPropagation();
    }

    if (environment.features.liveCasino.livespins) {
      if (gameName == "Bet Behind") {
        setTimeout(() => {
          if (
            gameName &&
            (this.getIsRealGame(gametype) ||
              this.getIsDemoGame(gametype, hasDemo))
          ) {
            this.mixpanelService.trackGameLanuchEvent({
              gameData: this.gameData,
              gameType: gametype,
              groupName: this.gameGroupName,
              lobbyName: this.lobbyName,
              callingFrom: this.callingFrom,
              activeTag: this.activeTagName,
            });

            this.router.navigate([`${this.languageCode}/livespins`]);
          } else if (gameName) {
            this.onTriggerLogin(gameName);
          }
        });

        return;
      }
    }

    gameName = this.utility.convertGameNameToUrl(gameName);

    let currentGameSlugList: string[] = this.utility
      .getDecodedCurrentPath()
      .split("/");

    if (currentGameSlugList[currentGameSlugList.length - 1] != gameName) {
      this.gamePlayService.clearGameWindowData();

      if (gametype === "freegame") {
        event.stopPropagation();
      }

      this.gamePlayService.setGameCalledfrom(gametype);

      setTimeout(() => {
        if (
          gameName &&
          (this.getIsRealGame(gametype) ||
            this.getIsDemoGame(gametype, hasDemo))
        ) {
          this.mixpanelService.trackGameLanuchEvent({
            gameData: this.gameData,
            gameType: gametype,
            groupName: this.gameGroupName,
            lobbyName: this.lobbyName,
            callingFrom: this.callingFrom,
            activeTag: this.activeTagName,
          });

          this.router.navigate([
            `${this.languageCode}/${this.translationService.instant(
              "url.game"
            )}/${gameName}`,
          ]);
        } else if (gameName) {
          this.onTriggerLogin(gameName);
        }
      });
    } else if (
      currentGameSlugList[currentGameSlugList.length - 1] === gameName &&
      gametype != this.gamePlayService.getGameCalledfrom()
    ) {
      if (gametype === "freegame") {
        event.stopPropagation();
      }

      // user to set value like realgame or freegame/demogame
      this.gamePlayService.setGameCalledfrom(gametype);

      /*
        Below Event is used to reload or switch the game from demo to real & viva versa
        we need this event because when user is in game play window &
        playing 7 piggies(demo game) & what to play realgame by click on top btn or from tag managment
        angular will not understand to reload route as URL in both cases will be same
        so we use this event method to force relaod game window iframe
      */
      this.gamePlayService.broadCastRelanuchGame(true);
    }

    /*
      Below code help you to close the search window after user
      launch any game from search area.
    */
    if (
      this.callingFrom === "recent-search" ||
      this.callingFrom === "game-window-tag"
    ) {
      this.callBackAction.emit();
    }
  }

  onTriggerLogin(gameName: string): void {
    let gameDetails: TriggerLoginGameDetails = {
      navigateTo: gameName == "Bet Behind" ? "livespins" : "gameWindow",
      path: gameName,
    };

    if (this.gameGroupName) {
      gameDetails.gameGroupName = this.gameGroupName;
    }

    if (this.lobbyName) {
      gameDetails.lobbyName = this.lobbyName;
    }

    this.commonService.setNavigateAfterLogin({
      type: "url",
      data: gameDetails,
    });

    this.utility.initiateLogin();
  }

  onJackpotNumberCount(): void {
    let self = this;

    $(".jamount").each(function () {
      $(this)
        .prop("Counter", 0)
        .animate(
          {
            Counter: $(this).text(),
          },
          {
            duration: 5000,
            easing: "swing",
            step: function (now) {
              if (!isNaN(now))
                $(this).text(
                  self.currencyPipe.transform(
                    now,
                    this.currencySymbol,
                    true
                  )
                );
            },
          }
        );
    });
  }

  // -----------------------------------------------------------------
  // On Destroy
  ngOnDestroy(): void {
    if (this.mircoGamingJackpotDataSubscription)
      this.mircoGamingJackpotDataSubscription.unsubscribe();

    this.subscriptions.forEach((subscription: Subscription) =>
      subscription.unsubscribe()
    );

    if (this.gameLauncherTimeout) clearTimeout(this.gameLauncherTimeout);
    if (this.timeout) clearTimeout(this.timeout);
  }
}
